// The DEMO practice cards behind the Requests page (founder, 1.5.1 #6): two
// cards, both in Pending from the start since 1.5.2 b49 — the rules live in
// lib/demoRequest. Loaded from the
// profile fetch, advanced optimistically on every answer, synced to the
// account fire-and-forget (a failed sync never shows an error; worst case a
// card comes back on a later sign-in). Never an Order, never money. A
// practice decline goes through the real reasons sheet marked PRACTICE
// (founder, build 44); the page calls decline() once an answer is picked, and
// the answer itself goes nowhere — no fan, no message.
import { useCallback, useState } from "react";
import type { User } from "../types/User";
import { useAccountCache } from "./useAccountCache";
import {
  advanceDemo,
  advanceDemoRequest,
  demoAsk,
  demoCardsFrom,
  finishedDemos,
  pendingDemo,
  pendingDemos,
  queuedDemos,
  type DemoCards,
  type DemoStatus,
  type DemoWhich,
} from "../lib/demoRequest";

export function useDemoRequests(slug: string | undefined, onFinished: () => void) {
  // Where the practice stands paints at once on a later visit (the profile
  // fetch below still re-reads it from the account).
  const [cards, setCards] = useAccountCache<DemoCards | null>("reqs-demo", null);
  // "You got it!" — once, when the answer just given handled the last card.
  const [celebrating, setCelebrating] = useState(false);

  const answer = (which: DemoWhich, to: Exclude<DemoStatus, null>) => {
    if (!slug || !cards) return;
    const next = advanceDemo(cards, which, to);
    setCards(next);
    advanceDemoRequest(slug, to, which);
    if (finishedDemos(next, to)) {
      setCelebrating(true);
      onFinished();
    }
  };

  // Stable, so the page's profile fetch can depend on it without re-running.
  const load = useCallback((me: User) => setCards(demoCardsFrom(me)), [setCards]);

  const pending = cards && pendingDemo(cards);
  return {
    cards,
    load,
    /** The first card in Pending and the gesture it asks for, or null. */
    pending: cards && pending ? { which: pending, ask: demoAsk(cards, pending) } : null,
    /** Every card in Pending, card 1 first, each with its gesture. */
    pendings: cards ? pendingDemos(cards).map((which) => ({ which, ask: demoAsk(cards, which) })) : [],
    queued: cards ? queuedDemos(cards) : [],
    accept: (which: DemoWhich) => answer(which, "queued"),
    play: (which: DemoWhich) => answer(which, "played"),
    decline: (which: DemoWhich) => answer(which, "declined"),
    celebrating,
    endCelebration: () => setCelebrating(false),
  };
}
